/**
 * Login.js
 * Company login page for the Procurement Intelligence Platform
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import WingmanLogo from '../components/WingmanLogo';
import { API_BASE } from '../config';

// Friendly copy for the short machine codes the SSO backend redirects back
// with (?sso_error=). Codes carry no detail by design; the detail is in the
// server logs.
const SSO_ERROR_COPY = {
  not_configured: "Single sign-on isn't set up for your company yet. Sign in with your password instead.",
  unavailable: 'Single sign-on is temporarily unavailable. Sign in with your password or try again shortly.',
  account_disabled: 'Your account has been deactivated. Contact your administrator.',
  auth_failed: "Microsoft sign-in didn't complete. Please try again.",
  provider_unreachable: "Microsoft sign-in didn't complete. Please try again.",
  expired: 'That sign-in expired before it finished. Please try again.',
};

// Lane 1416: the same codes when password sign-in is switched off, so no
// message tells the user to use a password they can no longer use.
const SSO_ONLY_ERROR_COPY = {
  ...SSO_ERROR_COPY,
  not_configured: "Single sign-on isn't set up for your email's company. Contact your administrator.",
  unavailable: 'Single sign-on is temporarily unavailable. Please try again shortly.',
};

const colors = {
  deepBlue: '#051C2C',
  electricBlue: '#2251FF',
  cyan: '#00A9F4',
  white: '#FFFFFF',
  lightGray: '#F5F7FA',
  mediumGray: '#E1E5EB',
  textMuted: '#8A9BAE',
  darkText: '#1A2B3C',
  danger: '#DC2626',
};

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [ssoEnabled, setSsoEnabled] = useState(false);
  // Default true: if the config call fails the page looks exactly as before.
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(true);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    // Render the Microsoft button only when the backend says SSO is live
    // (flag + credentials). Any failure leaves the page exactly as before.
    fetch(`${API_BASE}/api/sso/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setSsoEnabled(Boolean(data?.sso_enabled));
        const passwordsOn = data?.password_login_enabled !== false;
        setPasswordLoginEnabled(passwordsOn);
        const ssoError = new URLSearchParams(location.search).get('sso_error');
        if (ssoError) {
          const copy = passwordsOn ? SSO_ERROR_COPY : SSO_ONLY_ERROR_COPY;
          setError(copy[ssoError] || copy.auth_failed);
        }
      })
      .catch(() => {
        const ssoError = new URLSearchParams(location.search).get('sso_error');
        if (ssoError) setError(SSO_ERROR_COPY[ssoError] || SSO_ERROR_COPY.auth_failed);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSsoClick = () => {
    setError('');
    const trimmed = email.trim();
    const domain = trimmed.includes('@')
      ? trimmed.split('@').pop().trim().toLowerCase()
      : '';
    if (!domain) {
      setError('Enter your work email above first, then use Microsoft sign-in.');
      return;
    }
    // Only the domain leaves the browser — never the full address.
    window.location.href = `${API_BASE}/api/sso/authorize?domain=${encodeURIComponent(domain)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordLoginEnabled) {
      // Lane 1416: single sign-on only. Enter on the email field starts it.
      if (ssoEnabled) handleSsoClick();
      else setError('Sign-in is temporarily unavailable. Please try again shortly.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, 'company');

      if (result.success) {
        if (result.user.user_type === 'admin') {
          navigate('/admin');
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${colors.deepBlue} 0%, #0a2a4a 50%, ${colors.electricBlue}40 100%)`,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.white,
        borderRadius: '16px',
        boxShadow: 'var(--shadow-lg)',
        width: '100%',
        maxWidth: '420px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <WingmanLogo size={32} fill="white" />
          </div>
          <h1 style={{
            color: colors.white,
            fontSize: 'var(--font-size-hero)',
            fontWeight: 'var(--font-weight-bold)',
            margin: '0 0 6px 0',
          }}>
            Wingman
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: 'var(--font-size-base)',
            margin: 0,
          }}>
            Sign in to your account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          {error && (
            <div style={{
              backgroundColor: `${colors.danger}15`,
              border: `1px solid ${colors.danger}30`,
              color: colors.danger,
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: 'var(--font-size-base)',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--font-size-base)',
              color: colors.darkText,
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              disabled={loading}
              autoComplete="email"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `2px solid ${colors.mediumGray}`,
                borderRadius: '12px',
                fontSize: 'var(--font-size-base)',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = colors.electricBlue}
              onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
            />
          </div>

          {passwordLoginEnabled && (<>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--font-size-base)',
              color: colors.darkText,
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
              autoComplete="current-password"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `2px solid ${colors.mediumGray}`,
                borderRadius: '12px',
                fontSize: 'var(--font-size-base)',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = colors.electricBlue}
              onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
            />
          </div>

          <div style={{ marginBottom: '24px', textAlign: 'right' }}>
            <Link
              to="/forgot-password"
              style={{
                color: colors.textMuted,
                fontSize: 'var(--font-size-sm)',
                textDecoration: 'none',
              }}
            >
              Forgot your password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
              color: colors.white,
              border: 'none',
              borderRadius: '12px',
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${colors.electricBlue}50`;
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          </>)}

          {ssoEnabled && (
            <>
              {passwordLoginEnabled && <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: '20px 0 16px',
              }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.mediumGray }} />
                <span style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>or</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: colors.mediumGray }} />
              </div>}
              <button
                type="button"
                onClick={handleSsoClick}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: colors.white,
                  color: colors.darkText,
                  border: `2px solid ${colors.mediumGray}`,
                  borderRadius: '12px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                {/* Microsoft logo — four squares */}
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                Sign in with Microsoft
              </button>
            </>
          )}
        </form>

      </div>
    </div>
  );
}

export default Login;
